<template>
  <div class="">
    <Header v-if="!editing_packaging">
      <template #actions>
        <BasePushButton
          @click="addPackaging"
          variant="fill"
          icon="plus"
          size="sm"
          textColor="content-base-inverse-primary"
          bgColor="background-success-default"
        >
          {{ $t("nomenculature_packaging.btn_add") }}
        </BasePushButton>
      </template>
    </Header>

    <Header
      v-else
      :parentText="$t(`navbar.nomenculature_packaging`)"
      @toParent="editing_packaging = null"
    >
      <template #title>
        {{ editing_packaging.name || $t("nomenculature_packaging.btn_add") }}
      </template>
    </Header>

    <IQOSTable
      :rows="packagings"
      :cols="[
        {
          title: $t('nomenculature_packaging.col_name'),
          key: 'name',
        },
        {
          title: $t('nomenculature_packaging.col_price'),
          key: 'price',
          currency: 'symbol',
          default: '0'
        },
        {
          title: $t('nomenculature_packaging.col_weight'),
          key: 'max_weight',
          unit: $t('units.kg'),
          default: '0'
        },
      ]"
      :hasActions="true"
      v-if="!editing_packaging"
    >
      <template #actions="slotProps">
        <BaseButton
          @click="editing_packaging = slotProps.rowItem"
          variant="secondary"
          icon="pencil"
          size="sm"
          color="v1-grey-3"
        />
        <BaseButton
          @click="showRemoveUI(slotProps.rowItem._id)"
          variant="secondary"
          icon="trash"
          size="sm"
          color="v1-grey-3"
          class="ml-8"
        />
      </template>
    </IQOSTable>

    <PageContainer v-if="editing_packaging">
      <div class="iqos-grid">
        <div class="iqos-column">
          <IQOS class="gap-row-xs" required>
            <div class="gap-row-1xs">
              <div class="qrw-label-sm v1-grey-7-color bold">
                {{ $t("nomenculature_packaging.col_name") }}
              </div>
              <BaseInput v-model="editing_packaging.name" fullWidth />
            </div>
            <BaseDivider color="v1-grey-4" />
            <div class="gap-row-1xs">
              <div class="qrw-label-sm v1-grey-7-color bold">
                {{ $t("nomenculature_packaging.col_price") }}
              </div>
              <BaseUnitInput
                type="number"
                v-model.number="editing_packaging.price"
                fullWidth
                :unitPlaceholder="currency.symbol"
              />
            </div>
            <BaseDivider color="v1-grey-4" />
            <div class="gap-row-1xs">
              <div class="qrw-label-sm v1-grey-7-color bold">
                {{ $t("nomenculature_packaging.col_weight") }}
              </div>
              <BaseUnitInput
                type="number"
                v-model.number="editing_packaging.max_weight"
                fullWidth
                :unitPlaceholder="$t('units.kg')"
              />
            </div>
            <BaseDivider color="v1-grey-4" />
            <div class="gap-row-1xs">
              <div class="qrw-label-sm v1-grey-7-color bold">
                {{ $t("nomenculature_packaging.col_tax") }}
              </div>
              <BaseSelect
                type="number"
                v-model="editing_packaging.tax_id"
                fullWidth
              >
                <option :value="null">
                  {{ $t('tax_selector.no_tax') }}
                </option>
                <option v-for="tax in taxes" :key="tax._id" :value="tax._id">
                  {{ tax.name }} ({{tax.percent || 0}}%)
                </option>
              </BaseSelect>
            </div>
            <BaseDivider color="v1-grey-4" v-if="hasPosIntegration"/>
            <div class="gap-row-1xs" v-if="hasPosIntegration">
              <div class="qrw-label-sm v1-grey-7-color bold">
                {{ $t("nomenculature_packaging.param_pos_id") }}
              </div>

              <PosterMenuInput v-model="editing_packaging.poster_product_id" v-if="hasPosterIntegration"/>
              <IikoMenuInput v-model="editing_packaging.iiko_product_id" v-if="hasIikoIntegration"/>
            </div>
            <BaseDivider color="v1-grey-4" />
            <div class="packaging-form-controls">
              <BaseButton
                @click="save"
                variant="primary"
                bold
                size="md"
                icon="check"
                color="green-5"
              >
                {{ $t("nomenculature_packaging.btn_save") }}
              </BaseButton>
            </div>
          </IQOS>
        </div>
      </div>
    </PageContainer>

    <DangerConfirmModal
      @confirm="confirmRemove"
      @cancel="remove_id = null"
      v-if="remove_id"
    >
      {{ $t("nomenculature_packaging.confirm_remove") }}
    </DangerConfirmModal>
  </div>
</template>

<script>
import NomenculaturePackagingAPI from "@/api/nomenculature/packaging.js";
import TaxesAPI from "@/api/taxes"

import DangerConfirmModal from "@/components/modals/DangerConfirmModal.vue"

import PosterMenuInput from "@/components/integrations/PosterMenuInput.vue"
import IikoMenuInput from "@/components/integrations/IikoMenuInput.vue"

export default {
  data() {
    return {
      packagings: [],
      taxes: [],

      editing_packaging: false,
      remove_id: null
    };
  },
  methods: {
    fetchList() {
      NomenculaturePackagingAPI.getPackagings(this.cafe._id)
        .then((data) => {
          this.packagings = data.packagings;
        })
        .catch((err) => {});
    },
    confirmRemove() {
      NomenculaturePackagingAPI.removePackaging(this.remove_id)
        .then((data) => {
          this.remove_id = null;

          this.fetchList();
        })
        .catch((err) => {});
    },
    showRemoveUI(id) {
      this.remove_id = id;
    },
    addPackaging() {
      NomenculaturePackagingAPI.createPackaging({
        cafe_id: this.cafe._id,
        name: "",
        price: 0,
        max_weight: 0,
        tax_id: null,
      })
        .then((data) => {
          this.editing_packaging = { ...data.packaging };
          this.packagings.push(data.packaging);
        })
        .catch((err) => {});
    },
    save() {
      NomenculaturePackagingAPI.updatePackaging(this.editing_packaging._id, {
        name: this.editing_packaging.name,
        price: this.editing_packaging.price,
        max_weight: this.editing_packaging.max_weight,
        volume: this.editing_packaging.volume,
        tax_id: this.editing_packaging.tax_id,
        poster_product_id: this.editing_packaging.poster_product_id,
        iiko_product_id: this.editing_packaging.iiko_product_id,
      })
        .then((data) => {
          this.editing_packaging = null;

          this.fetchList();
        })
        .catch((err) => {});
    },
    fetchTaxes() {
      TaxesAPI.getTaxes(this.cafe._id).then((data) => {
        this.taxes = data.taxes;
      });
    },
  },
  computed: {
    cafe() {
      return this.$store.state.cafe;
    },
    currency() {
      return this.$store.state.currency;
    },
    hasPosterIntegration() {
      return this.$store.getters.hasPosterIntegration;
    },
    hasIikoIntegration() {
      return this.$store.getters.hasIikoIntegration;
    },
    hasPosIntegration() {
      return this.hasPosterIntegration || this.hasIikoIntegration;
    }
  },
  mounted() {
    this.fetchList();
    this.fetchTaxes();

    if (this.hasPosterIntegration) {
      this.$store.dispatch("poster/fetchProductsList");
    }

    if (this.hasIikoIntegration) {
      this.$store.dispatch("iiko/fetchProductsList");
    }
  },
  components: {
    DangerConfirmModal,
    PosterMenuInput,
    IikoMenuInput
  }
};
</script>

<style scoped>
.packaging-form-controls {
  display: flex;
  justify-content: center;
}
</style>
